import * as Track from "@axon-enterprise/track";
import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import {
  TEMP_MX_PROPERTIES,
  mixpanelService,
} from "../common/tracking/MixpanelService";
import { QRRedirectLayout } from '../components';
import { constructMDNSUrl, attemptMDNSRedirect } from '../utils/clientSideSerial/mdnsRedirect';
import { deriveModelFromSerial, normalizeSerial } from '../utils/clientSideSerial/serialToModel';
import { SOURCE_QR_CODE } from "../utils/constants";
import { clearLocalStorageOnDeviceMismatch, CORE_SETUP_KEYS } from '../utils/localStorage';

/**
 * V2 QR Redirect Page
 *
 * Handles QR code scanning for V2 route using client-side serial resolution.
 * Flow:
 * 1. Extract serial from URL
 * 2. Derive model using client-side pattern matching
 * 3. Store in localStorage
 * 4. If successful: redirect to mDNS (http://serial.local/)
 * 5. If unsuccessful: redirect to /v2/guide (shows dropdown)
 */
const V2QRRedirectPage: React.FC = () => {
  const { sku, serial } = useParams<{ sku?: string; serial?: string }>();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const history = useHistory();

  useEffect(() => {
    if (!serial) return;

    const processQRCode = async () => {
      const normalizedSerial = normalizeSerial(serial);
      const expiresAt = Date.now() + (24 * 60 * 60 * 1000);

      // Clear any mismatched data
      clearLocalStorageOnDeviceMismatch(serial, sku);

      // Set analytics context
      mixpanelService.setMetadata({
        [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: normalizedSerial,
        [Track.MxPropertyName.Source]: SOURCE_QR_CODE,
      });

      // Store serial and SKU
      localStorage.setItem(CORE_SETUP_KEYS.DEVICE_SERIAL_NUMBER, JSON.stringify({
        value: normalizedSerial,
        expiresAt,
      }));

      if (sku) {
        localStorage.setItem(CORE_SETUP_KEYS.DEVICE_SKU, JSON.stringify({
          value: sku,
          expiresAt,
        }));
      }

      // Derive model from serial
      const derivationResult = deriveModelFromSerial(normalizedSerial);

      if (derivationResult.success && derivationResult.model) {
        // Model derived - store and redirect to mDNS
        localStorage.setItem(CORE_SETUP_KEYS.DERIVED_MODEL, JSON.stringify({
          value: derivationResult.model,
          expiresAt,
        }));

        mixpanelService.track(Track.MxEventName.SearchByDeviceSerialNumber, {
          [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: normalizedSerial,
          [Track.MxPropertyName.Model]: derivationResult.model,
          [Track.MxPropertyName.Status]: 'success',
          [Track.MxPropertyName.Source]: SOURCE_QR_CODE,
        });

        // Try mDNS redirect first, fall back to 5s redirect if it fails
        setTimeout(async () => {
          const mdnsResult = await attemptMDNSRedirect(normalizedSerial, { timeout: 3000 });

          // If mDNS redirect failed, fall back to showing redirect page with 5s delay
          if (!mdnsResult.success) {
            const mdnsUrl = constructMDNSUrl(normalizedSerial);
            setRedirectUrl(mdnsUrl);
          }
        }, 1000);
      } else {
        // Could not derive model - go to guide to show dropdown
        mixpanelService.track(Track.MxEventName.SearchByDeviceSerialNumber, {
          [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: normalizedSerial,
          [Track.MxPropertyName.Status]: 'model_confirmation_required',
          [Track.MxPropertyName.Source]: SOURCE_QR_CODE,
        });

        history.push('/v2/guide');
      }
    };

    processQRCode();
  }, [serial, sku, history]);

  return <QRRedirectLayout serial={serial} redirectUrl={redirectUrl} />;
};

export default V2QRRedirectPage;
